import { c as _c } from "react/compiler-runtime";
import { ThemeProvider } from "@mui-wrapper";
import { useLayoutEffect } from "react";
import { useFaroColorScheme } from "@flat-ui";
import { useEffectiveColorSchemeMode } from "@hooks/use-effective-theme-mode";
import { dashboardTheme } from "@styles/themes";

/**
 * Applies the app's theme-mode setting to MUI's active colour scheme.
 *
 * This has to stay a child of the `ThemeProvider` below rather than fold into it:
 * `useFaroColorScheme` is MUI's `useColorScheme`, and it reads the colour-scheme context that same
 * provider creates. Called from `DynamicThemeProvider` the hook would sit above that context and
 * fall back to a no-op `setMode` - no error and no warning, just a theme setting that silently
 * stops applying.
 */
function ColorSchemeSync(t0) {
  const $ = _c(6);
  const {
    children
  } = t0;
  const themeMode = useEffectiveColorSchemeMode();
  const {
    setMode
  } = useFaroColorScheme();
  let t1;
  let t2;
  if ($[0] !== setMode || $[1] !== themeMode) {
    t1 = () => {
      setMode(themeMode);
    };
    t2 = [themeMode, setMode];
    $[0] = setMode;
    $[1] = themeMode;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useLayoutEffect(t1, t2);
  let t3;
  if ($[4] !== children) {
    t3 = <>{children}</>;
    $[4] = children;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}

/**
 * Theme provider that switches between the light and dark colour schemes based on the resolved
 * theme mode.
 *
 * Light and dark are two colour schemes of one flat-ui theme, so the theme is built once and the
 * scheme is switched on it. Rebuilding a theme per mode no longer works: with CSS theme variables
 * a `palette.mode` is only a label, and the palette that renders is whichever colour scheme is
 * active.
 *
 * `forceThemeRerender` recalculates the theme when the scheme changes, so `useTheme().palette` is
 * the active scheme's palette - which is what the `@flat-ui` wrappers read to set the `dark` prop,
 * and what every `sx` callback reading `theme.palette` assumes.
 *
 * `storageManager={null}` turns off MUI's own localStorage copy of the mode: the user's
 * `theme_mode` setting is the single source of truth, and a second store would win on first paint
 * before that setting has loaded.
 *
 * TODO: default this to `"system"` in https://faro01.atlassian.net/browse/ST-4789
 * `defaultMode="light"` is what renders before the user's settings have loaded and the beta flag
 * is known. Following the OS from the first frame would put a non-beta user on a dark OS into dark
 * mode, although their "system" setting resolves to light. `useEffectiveColorSchemeMode` applies
 * the saved setting once it loads, and hands `"system"` to `setMode` unresolved so MUI tracks the
 * OS query rather than the app resolving it separately.
 */
export function DynamicThemeProvider(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <ThemeProvider theme={dashboardTheme} defaultMode="light" storageManager={null} forceThemeRerender={true}><ColorSchemeSync>{children}</ColorSchemeSync></ThemeProvider>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}